import { isEmpty } from 'ramda';
import { useCallback } from 'react';
import { useParams } from 'react-router';

import { useAppDispatch, useAppSelector, useMedia } from '@/shared/hooks';
import { scrollToElementByID, trackEvent } from '@/shared/lib';
import { Skeleton } from '@/shared/ui';

import { reportsModel } from '@/entities/reports';
import { toothModel } from '@/entities/tooth';

import {
  ToothChart,
  ToothChartItemClickHandler,
  toothChartModel,
} from '@/features/toothChart';

import styles from './GpToothChart.module.scss';

export const GpToothChart = () => {
  const { reportID } = useParams();

  const { isMobile } = useMedia();

  const dispatch = useAppDispatch();

  const { toothItems, toothTypesSet, isFDA3DGPRestrictions } = useAppSelector(
    toothChartModel.selectors.selectToothChartItems(reportID as string),
  );

  const customMode = useAppSelector(
    toothModel.selectors.selectToothChartCustomMode,
  );

  const isToothChartReady = !isEmpty(toothTypesSet);

  const handleToothClick = useCallback<ToothChartItemClickHandler>(
    ({ id, active, ISONumber }) => {
      if (customMode) {
        dispatch(toothModel.actions.toggleLocalROITeethIDs([id]));
      } else if (active) {
        trackEvent({
          eventName: 'ReportPageToothFormulaButtonClicked',
          properties: {
            tooth_number: ISONumber,
          },
        });

        scrollToElementByID(id);
        dispatch(reportsModel.actions.setActiveToothID(id));
      }
    },
    [customMode, dispatch],
  );

  return (
    <ToothChart.Root
      size={isMobile ? 'mobile' : 'default'}
      className={styles.container}
    >
      {!isMobile && (
        <ToothChart.Header hideTitle={isMobile}>
          <ToothChart.CustomModeToggle />
        </ToothChart.Header>
      )}

      {isToothChartReady ? (
        <>
          {!isMobile && (
            <ToothChart.Tags
              toothTypesSet={toothTypesSet}
              reportID={reportID as string}
              isFDAMode={isFDA3DGPRestrictions}
            />
          )}

          <ToothChart.Grid>
            {toothItems.map((toothItem) => (
              <ToothChart.MetaItem
                key={toothItem.id}
                toothItem={toothItem}
                handleToothClick={handleToothClick}
              />
            ))}
          </ToothChart.Grid>
        </>
      ) : (
        <>
          <Skeleton.Filter
            filtersQuantity={4}
            height={20}
            gap={4}
            borderRadius={10}
          />

          <Skeleton width={582} height={184} borderRadius={8} />
        </>
      )}
    </ToothChart.Root>
  );
};
